import { useCallback, useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'

import { getUserId } from '@/helpers/getUserId'
import { getFavoriteListIdsLoader } from '@/hooks/bff/favorite/getAllFavoriteListIdsByProducts/favoriteListIdsBatchLoader'
import { getAllFavoriteListIdsByProductKeys } from '@/hooks/bff/favorite/getAllFavoriteListIdsByProducts/queryKeys'
import { useAsmSession } from '@/hooks/useAsmSession'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useTranslations } from '@/hooks/useTranslations'

interface UseGetAllFavoriteListIdsByProductResult {
  /** Favorite list ids per product id. A product still loading is absent from the map. */
  favoriteListIdsByProduct: Record<string, string[]>
  isFetching: boolean
}

interface UseGetAllFavoriteListIdsByProductOptions {
  refetchOnMount?: boolean | 'always'
}

export const useGetAllFavoriteListIdsByProduct = (
  productIds: string[],
  options?: UseGetAllFavoriteListIdsByProductOptions
): UseGetAllFavoriteListIdsByProductResult => {
  const { translate } = useTranslations()
  const { session, isAuthorized } = useAsmSession()
  const customerId = getUserId(session)
  const orgUnitId = useSelectedOrgUnitId()

  const productIdsKey = productIds.join('|')
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on productIdsKey, a stable proxy for productIds' contents, since productIds is a new array reference every render
  const uniqueProductIds = useMemo(() => [...new Set(productIds.filter(Boolean))], [productIdsKey])

  const loader = useMemo(() => getFavoriteListIdsLoader(customerId, orgUnitId), [customerId, orgUnitId])

  const queries = useMemo(
    () =>
      uniqueProductIds.map((productId) => ({
        queryKey: getAllFavoriteListIdsByProductKeys.product(customerId, orgUnitId, productId),
        queryFn: () => loader.load(productId),
        enabled: isAuthorized,
        refetchOnMount: options?.refetchOnMount,
        meta: { errorMessage: translate('common.api_error') },
      })),
    [uniqueProductIds, loader, customerId, orgUnitId, isAuthorized, translate, options?.refetchOnMount]
  )

  // combine must stay referentially stable: useQueries feeds it to useSyncExternalStore, and a new combine
  // every render creates a new snapshot every time, causing an infinite re-render loop.
  const combine = useCallback(
    (results: Array<{ data?: string[]; isFetching: boolean }>): UseGetAllFavoriteListIdsByProductResult => {
      const favoriteListIdsByProduct: Record<string, string[]> = {}

      results.forEach((result, index) => {
        if (result.data) {
          favoriteListIdsByProduct[uniqueProductIds[index]] = result.data
        }
      })

      return { favoriteListIdsByProduct, isFetching: results.some((result) => result.isFetching) }
    },
    [uniqueProductIds]
  )

  return useQueries({ queries, combine })
}
